import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { AccountFrame } from "@/features/accounts/shared";
import { ApiError, verifyLoginLink } from "@/lib/api";

export const Route = createFileRoute("/login/verify")({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({ token: typeof search.token === "string" ? search.token : undefined }),
  component: VerifyPage,
});

/**
 * The emailed link lands here; the token is spent by a POST from this page, never by the GET
 * (mail scanners open links to check them).
 */
function VerifyPage() {
  const { token } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    document.title = "Signing in · traces.insure";
    if (started.current) return;
    started.current = true;
    if (!token) {
      setError("This sign-in link is incomplete. Copy the whole link from the email, or request a new one.");
      return;
    }
    verifyLoginLink(token)
      .then(({ next }) => window.location.replace(next || "/"))
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : "We couldn't sign you in. Request a new link."));
  }, [token]);

  return (
    <AccountFrame>
      <div className="mx-auto flex w-full max-w-sm flex-col gap-4 pt-10">
        <Card>
          <CardBody className="flex flex-col items-start gap-3">
            {error ? (
              <>
                <h1 className="text-lg font-semibold tracking-tight">This link didn't work</h1>
                <p role="alert" className="text-[13px] text-fg-2">
                  {error}
                </p>
                <Button asChild size="sm">
                  <Link to="/login">Request a new link</Link>
                </Button>
              </>
            ) : (
              <p className="flex items-center gap-2 text-[13px] text-fg-2" aria-live="polite">
                <Loader2 className="size-4 animate-spin text-accent" aria-hidden /> Signing you in…
              </p>
            )}
          </CardBody>
        </Card>
      </div>
    </AccountFrame>
  );
}
